import * as React from "react";
import { useTranslation } from "react-i18next";
import { UserCog, AlertTriangle } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api/client";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useNewTaskMutations } from "../hooks";
import { getMatterMembershipRole, hasRunningActivity } from "../utils";
import type { Task } from "../types";

type Member = { id: number; name: string; role?: { type?: string } };

interface MatterData {
  id: number;
  responsibleAttorneys?: Member[];
  partners?: Member[];
  staffs?: Member[];
}

interface TargetOption {
  id: number;
  label: string;
}

interface ReassignStaffDialogProps {
  task: Task;
  /** Controlled open state (e.g. when launched from a "More" menu item). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button — the dialog is opened externally. */
  hideTrigger?: boolean;
  onSuccess?: () => void;
}

export function ReassignStaffDialog({
  task,
  open,
  onOpenChange,
  hideTrigger = false,
  onSuccess,
}: ReassignStaffDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : internalOpen;
  const setDialogOpen = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };
  const [matter, setMatter] = React.useState<MatterData | null>(null);
  const [isLoadingMatter, setIsLoadingMatter] = React.useState(false);
  const [staffId, setStaffId] = React.useState<number | undefined>(undefined);
  const [confirmRunningOpen, setConfirmRunningOpen] = React.useState(false);

  const { reassignStaffMutation } = useNewTaskMutations();

  React.useEffect(() => {
    if (!dialogOpen) {
      setMatter(null);
      setStaffId(undefined);
      setConfirmRunningOpen(false);
      return;
    }
    if (!task.matterId) {
      setMatter(null);
      return;
    }
    let cancelled = false;
    setIsLoadingMatter(true);
    api
      .get<MatterData>(`/matters/${task.matterId}`)
      .then((data) => {
        if (!cancelled) setMatter(data);
      })
      .catch(() => {
        if (!cancelled) setMatter(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingMatter(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, task.matterId]);

  // Caller's authority on this matter (+ global MD override) decides who can be a target:
  //  - MD / partner → any matter member (incl. self), minus the current assignee
  //  - lead         → matter staff only
  const isMd = user?.role?.type === "MD";
  const callerRole = getMatterMembershipRole(matter ?? undefined, user?.id);

  const targetOptions = React.useMemo<TargetOption[]>(() => {
    if (!matter) return [];
    const staffs = matter.staffs ?? [];
    const attorneys = matter.responsibleAttorneys ?? [];
    const partners = matter.partners ?? [];

    const pool: Member[] =
      isMd || callerRole === "partner"
        ? [...staffs, ...attorneys, ...partners]
        : callerRole === "lead"
          ? // Lead may reassign to a matter staff or take the task over themselves.
            [...staffs, ...attorneys.filter((a) => a.id === user?.id)]
          : [];

    // Effective matter role per id — lead > partner > staff, matching the server's
    // status derivation (responsible attorney → LEAD_PENDING wins over partner).
    // Drives both the resulting status and the label shown to the user.
    const roleLabelFor = (memberId: number): string => {
      if (attorneys.some((a) => a.id === memberId))
        return t("roleMatterType.LEAD");
      if (partners.some((p) => p.id === memberId))
        return t("roleMatterType.PARTNER");
      return t("roleMatterType.STAFF_MEMBER");
    };

    const uniqueMap = new Map<number, TargetOption>();
    for (const person of pool) {
      if (person?.id == null) continue;
      if (person.id === task.assignedStaffId) continue; // exclude current assignee (no-op)
      // A task can only go to an MD via MD self-assign — hide every other MD.
      const memberIsMd = person.role?.type === "MD";
      if (memberIsMd && !(isMd && person.id === user?.id)) continue;
      if (uniqueMap.has(person.id)) continue;
      uniqueMap.set(person.id, {
        id: person.id,
        label: `${person.name} — ${roleLabelFor(person.id)}`,
      });
    }
    return Array.from(uniqueMap.values());
  }, [matter, isMd, callerRole, task.assignedStaffId, user?.id, t]);

  const isRunning = hasRunningActivity(task.activities);
  const canSubmit = staffId != null;

  const doReassign = () => {
    if (staffId == null) return;
    reassignStaffMutation.mutate(
      { id: task.id, staffId },
      {
        onSuccess: () => {
          setConfirmRunningOpen(false);
          setDialogOpen(false);
          onSuccess?.();
        },
      },
    );
  };

  const handleSubmit = () => {
    if (staffId == null) return;
    // A running timer is deleted on reassign — warn first.
    if (isRunning) {
      setConfirmRunningOpen(true);
      return;
    }
    doReassign();
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <UserCog className="h-4 w-4" />
            {t("reassignStaff")}
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("reassignStaff")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("reassignStaff")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="reassign-staff-select">{t("assignedTo")}</Label>
          <Select
            value={staffId != null ? String(staffId) : ""}
            onValueChange={(val) => setStaffId(val ? Number(val) : undefined)}
            disabled={isLoadingMatter || targetOptions.length === 0}
          >
            <SelectTrigger id="reassign-staff-select">
              <SelectValue placeholder={t("selectStaff")} />
            </SelectTrigger>
            <SelectContent>
              {targetOptions.length > 0 ? (
                targetOptions.map((option) => (
                  <SelectItem key={option.id} value={String(option.id)}>
                    {option.label}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value="no-staff">
                  {t("noAvailableStaff")}
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          {/* Completed activities are preserved as an adjustment on reassign. */}
          <p className="text-muted-foreground text-xs">
            {t("reassignActivityHint")}
          </p>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              disabled={reassignStaffMutation.isPending}
            >
              {t("cancel")}
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || reassignStaffMutation.isPending}
          >
            {reassignStaffMutation.isPending ? t("saving") : t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Running-timer warning — confirm before the running activity is deleted. */}
      <AlertDialog open={confirmRunningOpen} onOpenChange={setConfirmRunningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-destructive h-5 w-5" />
              {t("reassignRunningTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("reassignRunningWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reassignStaffMutation.isPending}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doReassign();
              }}
              disabled={reassignStaffMutation.isPending}
            >
              {reassignStaffMutation.isPending
                ? t("saving")
                : t("reassignStaff")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
